import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { useMutation, useQuery } from 'react-query'
import cloneDeep from 'lodash.clonedeep'

import { usePagination } from '@hooks/usePagination'
import { OrderedKeyEnum } from '@interfaces/OrderedEnum'
import { PrimaryTypeEnum } from '@interfaces/PrimaryTypeEnum'
import { useAuthentication } from '@hooks/useAuthentication'
import { useClient } from '@hooks/useClient'
import { scrollToElement } from '@lib/utils'
import { MenuTypeEnum } from '@interfaces/MenuTypeEnum'
import { queryClient } from '@client/init'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { SearchRecommendationResponse } from '@models/recommend/SearchRecommendationResponse'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useBookAction } from '@hooks/book/useBookAction'
import { SortEnum } from '@interfaces/SortEnum'
import { useCategoryAction } from '@hooks/category/useCategoryAction'
import {
  CategoryListAcceptProps,
  CategoryListProps,
  OptionFormProps,
} from './interface'

const withCategoryList = (Component: React.FC<CategoryListProps>) => {
  function Hoc({
    tab,
    orderBy,
    secondaryType,
    isEbook,
    advanceSearch,
  }: CategoryListAcceptProps) {
    const categoryClient = useCategoryAction()
    const bookClient = useBookAction()
    const { isAuthenticated } = useAuthentication()
    const loginModal = useModal({ modal: 'login' })
    const { query, pathname, replace } = useRouter()
    const [recommendationSearch, setRecommendationSearch] =
      useState<PrimaryTypeEnum>(tab)
    const [showOption, setShowOption] = useState(advanceSearch)
    const [searchParams, setSearchParams] = useState<OptionFormProps>({
      ordered: orderBy,
      recommendTypeSearch: secondaryType,
    })
    const { isUserLoading, canFetchApi } = useAuthentication()
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const { data, isLoading } = useQuery(
      [
        'category',
        query.id,
        query?.bookType,
        recommendationSearch,
        searchParams,
        page,
        perpage,
      ],
      () =>
        categoryClient.getCategoryList({
          allCategorySearchParam: {
            categoryId: query?.id as string,
            primaryType: recommendationSearch,
            orderBy: searchParams.ordered,
            secondaryType: searchParams.recommendTypeSearch,
          },
          page,
          perpage,
          bookType: query?.bookType as MenuTypeEnum,
        }),
      {
        enabled: canFetchApi,
      }
    )

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation((id: number) => bookClient.toggleBookLike(id), {
        onSuccess: (type, id) => {
          queryClient.setQueryData<SearchRecommendationResponse | undefined>(
            [
              'category',
              query.id,
              query?.bookType,
              recommendationSearch,
              searchParams,
              page,
              perpage,
            ],
            (oldData?: SearchRecommendationResponse) => {
              if (oldData) {
                const temp = cloneDeep(oldData.data)
                const index = temp.findIndex(row => row.id === id)

                if (index !== -1) {
                  temp[index].isLiked = type === LikeSuccessStatusEnum.CREATE
                  temp[index].likeCount =
                    type === LikeSuccessStatusEnum.CREATE
                      ? temp[index].likeCount + 1
                      : Math.max(0, temp[index].likeCount - 1)
                }

                return {
                  ...oldData,
                  data: temp,
                }
              }

              return oldData
            }
          )
        },
      })

    function handleRecommendationSearchChange(value: PrimaryTypeEnum) {
      const newQuery: any = {
        id: query.id,
        tab: value,
        orderBy: [PrimaryTypeEnum.NEW, PrimaryTypeEnum.ENDED].includes(value)
          ? `${OrderedKeyEnum.UPDATED_AT}:${SortEnum.DESC}`
          : value === PrimaryTypeEnum.TOP_CHART
          ? `${OrderedKeyEnum.PURCHASED}:${SortEnum.DESC}`
          : `${OrderedKeyEnum.POPULAR}:${SortEnum.DESC}`,
        advanceSearch: showOption,
      }

      if (query.bookType) newQuery.bookType = query.bookType

      replace(
        {
          pathname,
          query: newQuery,
        },
        undefined,
        {
          shallow: true,
        }
      )
      setRecommendationSearch(value)
      setSearchParams({
        ordered: newQuery.orderBy,
        recommendTypeSearch: undefined,
      })
      pageChange(1)
    }

    function toggleOption() {
      const newQuery: any = {
        id: query.id,
        tab: recommendationSearch,
        advanceSearch: !showOption,
      }

      if (query.bookType) newQuery.bookType = query.bookType

      if (showOption) {
        const ordered = [PrimaryTypeEnum.NEW, PrimaryTypeEnum.ENDED].includes(
          recommendationSearch
        )
          ? `${OrderedKeyEnum.UPDATED_AT}:${SortEnum.DESC}`
          : recommendationSearch === PrimaryTypeEnum.TOP_CHART
          ? `${OrderedKeyEnum.PURCHASED}:${SortEnum.DESC}`
          : `${OrderedKeyEnum.POPULAR}:${SortEnum.DESC}`

        setSearchParams({
          ordered,
          recommendTypeSearch: undefined,
        })

        newQuery.orderBy = ordered
      }

      replace(
        {
          pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
      setShowOption(prev => !prev)
    }

    async function handleSearch({
      ordered,
      recommendTypeSearch,
    }: OptionFormProps) {
      pageChange(1)
      setSearchParams({
        ordered,
        recommendTypeSearch,
      })

      const newQuery: any = { ...query, orderBy: ordered }
      if (recommendTypeSearch) newQuery.secondaryType = recommendTypeSearch

      replace(
        {
          pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    }

    function handlePageChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#category-list')
    }

    async function handleLikeBook(id: number) {
      if (isAuthenticated) {
        await toggleBookLike(id)
      } else {
        loginModal.show()
      }
    }

    const newProps = {
      recommendBooks: data?.data,
      recommendationSearch,
      tab,
      showOption,
      perpage,
      page,
      total: data?.total || 0,
      initialValues: searchParams,
      isEbook,
      isLoading: isLoading || isUserLoading,
      isLoadingToggleBookLike,
      handleRecommendationSearchChange,
      toggleOption,
      handleSearch,
      perpageChange,
      handleLikeBook,
      handlePageChange,
    }

    return <Component {...newProps} />
  }

  return Hoc
}

export { withCategoryList }
